import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { WorkforceBatch1Panel, DepartmentFilter } from "@/components/WorkforceBatch1";
import { Plus, ChevronLeft, ChevronRight, Users, CalendarDays, Download, AlertTriangle, Trash2, Pencil, Layers, Wand2, Repeat, Check, Copy, Eye, EyeOff } from "lucide-react";
import { ActiveShiftsPanel, TimesheetsPanel } from "@/components/AttendancePanels";
import { GeofencePanels } from "@/components/GeofencePanels";
import { LabourBudgetBar, PublishSummaryDialog } from "@/components/LabourBudget";
import { LeaveManager } from "@/components/LeavePanel";
import { LabourComparison } from "@/components/LabourComparison";
import { PlaceholderRow } from "@/components/PlaceholderRow";
import { IdentityReviews } from "@/components/IdentityReviews";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DEPTS = ["Kitchen", "Front of House", "Bar", "Cleaning", "Management", "Other"];

function mondayOf(d) { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return x; }
function iso(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function Workforce() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const canManage = user?.permissions_effective?.includes("manage_rota");
  const canManageWorkforce = user?.permissions_effective?.includes("manage_workforce");
  const canCreateLogin = canManageWorkforce && user?.permissions_effective?.includes("edit_staff");
  const canViewBudget = user?.permissions_effective?.includes("workforce.budget.view");
  const canViewTimesheets = user?.permissions_effective?.includes("workforce.timesheets.view");
  const [siteId, setSiteId] = useState("");
  useEffect(() => { api.get("/me/sites").then((r) => { const s = r.data.sites || []; setSites(s); setSiteId((prev) => prev || s[0]?.venue_id || ""); }).catch(() => {}); }, []);
  const [tab, setTab] = useState("rota");
  return (
    <div className="p-4 space-y-4" data-testid="workforce-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Workforce & Rota</h1>
        <Select value={siteId} onValueChange={setSiteId}>
          <SelectTrigger className="w-56" data-testid="workforce-site"><SelectValue placeholder="Select site" /></SelectTrigger>
          <SelectContent>{sites.map((s) => <SelectItem key={s.venue_id} value={s.venue_id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {user?.permissions_effective?.includes("identity.links.resolve") && <IdentityReviews />}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="rota" data-testid="tab-rota"><CalendarDays size={15} className="mr-1" /> Rota</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff"><Users size={15} className="mr-1" /> Staff</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="combined" data-testid="tab-combined"><Layers size={15} className="mr-1" /> All sites</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests</TabsTrigger>
          {canManage && <TabsTrigger value="live" data-testid="tab-live">Live</TabsTrigger>}
          {canManage && <TabsTrigger value="timesheets" data-testid="tab-timesheets">Timesheets</TabsTrigger>}
          {canManageWorkforce && <TabsTrigger value="leave" data-testid="tab-leave">Leave</TabsTrigger>}
          {canViewBudget && canViewTimesheets && <TabsTrigger value="labour" data-testid="tab-labour">Labour</TabsTrigger>}
        </TabsList>
        <TabsContent value="rota">{siteId ? <RotaBuilder siteId={siteId} canManage={canManage} canViewBudget={user?.permissions_effective?.includes("workforce.budget.view")} canManageBudget={user?.permissions_effective?.includes("workforce.budget.manage")} /> : <p className="text-slate-400">Select a site to view its rota.</p>}</TabsContent>
        <TabsContent value="staff">{siteId ? <StaffDirectory siteId={siteId} sites={sites} canManage={canManageWorkforce} canCreateLogin={canCreateLogin} /> : <p className="text-slate-400">Select a site.</p>}</TabsContent>
        <TabsContent value="templates"><TemplatesPanel sites={sites} canManage={canManage} /></TabsContent>
        <TabsContent value="combined"><CombinedPanel sites={sites} canManage={canManage} /></TabsContent>
        <TabsContent value="requests"><RequestsPanel canManage={canManage} /></TabsContent>
        {canManage && <TabsContent value="live"><ActiveShiftsPanel sites={sites} /><GeofencePanels sites={sites} /></TabsContent>}
        {canManage && <TabsContent value="timesheets"><TimesheetsPanel sites={sites} /></TabsContent>}
        {canManageWorkforce && <TabsContent value="leave"><LeaveManager siteId={siteId} /></TabsContent>}
        {canViewBudget && canViewTimesheets && <TabsContent value="labour">{siteId ? <LabourComparison siteId={siteId} canDraft={canManage} /> : <p className="text-slate-400">Select a site.</p>}</TabsContent>}
      </Tabs>
    </div>
  );
}

function RotaBuilder({ siteId, canManage, canViewBudget, canManageBudget }) {
  const { user } = useAuth();
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [weekStart, setWeekStart] = useState(iso(mondayOf(new Date())));
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [quickFill, setQuickFill] = useState(false);
  const [gaps, setGaps] = useState([]);
  const [showGaps, setShowGaps] = useState(false);
  const [dept, setDept] = useState("");
  const load = () => api.get(`/workforce/rota?site_id=${siteId}&week_start=${weekStart}${dept ? `&department=${encodeURIComponent(dept)}` : ""}`).then((r) => setData(r.data)).catch(() => setData(null));
  const loadGaps = () => api.get(`/workforce/coverage-gaps?site_id=${siteId}&week_start=${weekStart}`).then((r) => setGaps(r.data.gaps || [])).catch(() => setGaps([]));
  useEffect(() => { load(); loadGaps(); /* eslint-disable-next-line */ }, [siteId, weekStart, dept]);
  useEffect(() => { api.get("/workforce/templates").then((r) => setTemplates((r.data || []).filter((t) => t.active !== false))).catch(() => setTemplates([])); }, []);
  const days = useMemo(() => DAYS.map((_, i) => iso(addDays(new Date(weekStart), i))), [weekStart]);
  if (!data) return <p className="text-slate-400" data-testid="rota-loading">Loading rota…</p>;
  const rw = data.rota_week || {};
  const shiftsByPersonDay = {};
  (data.shifts || []).forEach((s) => { (shiftsByPersonDay[s.person_id] = shiftsByPersonDay[s.person_id] || {})[s.date] = [...(shiftsByPersonDay[s.person_id]?.[s.date] || []), s]; });
  const gapDays = new Set(gaps.map((g) => g.date));
  const doPublish = () => api.post("/workforce/rota/publish", { site_id: siteId, week_start: weekStart }).then(() => { setConfirmPublish(false); toast.success("Rota published"); load(); });
  const publish = () => (canViewBudget ? setConfirmPublish(true) : doPublish());
  const unpublish = () => api.post("/workforce/rota/unpublish", { site_id: siteId, week_start: weekStart }).then(() => { toast.success("Rota moved to draft"); load(); });
  const exportCsv = () => { window.open(`${api.defaults.baseURL}/workforce/rota/export.csv?site_id=${siteId}&week_start=${weekStart}`, "_blank"); };

  return (
    <div className="space-y-3">
      <WorkforceBatch1Panel siteId={siteId} weekStart={weekStart} department={dept} canManage={canManage}
        canExport={user?.permissions_effective?.includes("workforce.payroll.export")} refreshKey={data} onChanged={load} />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <DepartmentFilter value={dept} onChange={setDept} />
          <Button variant="outline" size="icon" data-testid="rota-prev" onClick={() => setWeekStart(iso(addDays(new Date(weekStart), -7)))}><ChevronLeft size={16} /></Button>
          <span className="font-semibold text-slate-700" data-testid="rota-week-label">Week of {weekStart}</span>
          <Button variant="outline" size="icon" data-testid="rota-next" onClick={() => setWeekStart(iso(addDays(new Date(weekStart), 7)))}><ChevronRight size={16} /></Button>
          <span className={`text-xs px-2 py-1 rounded-lg ${rw.status === "published" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`} data-testid="rota-status">
            {data.publication?.label || (rw.status === "published" ? "Published" : "Draft")}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="rota-export"><Download size={14} className="mr-1" /> Export CSV</Button>
          {canManage && <Button variant="outline" size="sm" data-testid="rota-copy-week" onClick={async () => {
            const prev = iso(addDays(new Date(weekStart), -7));
            try {
              const pv = (await api.post("/workforce/rota/copy-week", { site_id: siteId, from_week_start: prev, to_week_start: weekStart, preview: true })).data;
              const s = pv.summary;
              if (s.source_shifts === 0) return toast.message("No shifts in the previous week to copy");
              if (!window.confirm(`Copy ${s.ready} of ${s.source_shifts} shifts from ${prev}?\n${s.inactive} inactive/unassigned · ${s.conflicts} conflicts · ${s.unavailable} unavailable · ${s.already_exists} already exist`)) return;
              const res = (await api.post("/workforce/rota/copy-week", { site_id: siteId, from_week_start: prev, to_week_start: weekStart, operation_id: (crypto.randomUUID?.() || `op_${Date.now()}`), mode: "skip_conflicts" })).data;
              toast.success(`Copied ${res.created} shifts (${res.skipped} skipped)`); load();
            } catch (e) { toast.error(e.response?.data?.detail || "Copy failed"); }
          }}>Copy previous week</Button>}
          {canManage && templates.length > 0 && <Button variant="outline" size="sm" data-testid="rota-quick-fill" onClick={() => setQuickFill(true)}><Wand2 size={14} className="mr-1" /> Quick-fill</Button>}
          {canManage && (rw.status === "published"
            ? <>
                {(rw.has_unpublished_changes || data.publication?.unpublished_changes > 0) && <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={publish} data-testid="rota-republish">Publish changes</Button>}
                <Button variant="outline" size="sm" onClick={unpublish} data-testid="rota-unpublish">Unpublish</Button>
              </>
            : <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={publish} data-testid="rota-publish">Publish rota</Button>)}
      </div>

      {canViewBudget && <LabourBudgetBar siteId={siteId} weekStart={weekStart} refreshKey={data} canManageBudget={canManageBudget} />}
      {(data.pending_deletions || []).length > 0 && (
        <div className="text-xs border border-slate-200 rounded-xl p-3 space-y-1" data-testid="rota-pending-deletions">
          <div className="font-semibold text-slate-700">Removed shifts — staff still see these until you publish</div>
          {data.pending_deletions.map((s) => (
            <div key={s.shift_id} className="flex items-center justify-between gap-2" data-testid={`pending-delete-${s.shift_id}`}>
              <span className="line-through text-slate-500">{s.person_name} · {s.date} {s.start}–{s.end}</span>
              {canManage && <Button size="sm" variant="ghost" className="h-7" data-testid={`restore-shift-${s.shift_id}`} onClick={async () => { await api.post(`/workforce/shifts/${s.shift_id}/restore`); load(); }}>Keep shift</Button>}
            </div>
          ))}
        </div>
      )}
      {gaps.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-2xl" data-testid="coverage-banner">
          <button className="w-full flex items-center justify-between px-4 py-3 text-left" onClick={() => setShowGaps((v) => !v)} data-testid="coverage-banner-toggle">
            <span className="font-semibold text-amber-800 flex items-center gap-2"><AlertTriangle size={16} /> {gaps.length} shift{gaps.length > 1 ? "s" : ""} need{gaps.length === 1 ? "s" : ""} cover this week</span>
            <span className="text-xs text-amber-700 underline">{showGaps ? "Hide" : "View"}</span>
          </button>
          {showGaps && (
            <div className="border-t border-amber-200 divide-y divide-amber-100" data-testid="coverage-gap-list">
              {gaps.map((g) => (
                <div key={g.request_id} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`coverage-gap-${g.request_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800">{g.date} · {g.start}–{g.end}{g.role ? ` · ${g.role}` : ""}</div>
                    <div className="text-xs text-slate-500 truncate">{g.site_name} · {g.original_person_name}
                      {g.mode === "nominated" ? " · asked a colleague" : ` · ${g.interested_count} eligible`}
                      {g.age_hours != null ? ` · ${g.age_hours >= 24 ? Math.round(g.age_hours / 24) + "d" : Math.round(g.age_hours) + "h"} ago` : ""}</div>
                  </div>
                  <span className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-white text-amber-700 shrink-0">{g.status === "awaiting_manager" ? "Ready to approve" : "Awaiting colleague"}</span>
                </div>
              ))}
              <div className="px-4 py-2 text-xs text-slate-500">Approve or decline these in the <strong>Requests</strong> tab.</div>
            </div>
          )}
        </div>
      )}
        <div className="overflow-x-auto overscroll-x-contain -mx-1 px-1" data-testid="rota-week-scroll" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="text-left text-slate-500 border-b">
            <th className="px-3 py-2 sticky left-0 bg-white">Staff</th>
            {DAYS.map((d, i) => <th key={d} className="px-2 py-2">{d}<div className="text-[10px] text-slate-400">{days[i].slice(5)}</div>{gapDays.has(days[i]) && <div className="text-[9px] font-semibold text-amber-600 flex items-center gap-0.5" data-testid={`gap-day-${days[i]}`}><AlertTriangle size={9} /> cover</div>}</th>)}
            <th className="px-2 py-2">Hours</th>
          </tr></thead>
          <tbody>
            {(data.people || []).length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400" data-testid="rota-no-staff">No staff assigned to this site. Add them in the Staff tab.</td></tr>}
            {(data.people || []).map((p) => {
              const totals = data.hours_summary?.by_person?.find((r) => r.person_id === p.person_id);
              return (
                <tr key={p.person_id} className="border-b align-top" data-testid={`rota-row-${p.person_id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white font-medium text-slate-700 whitespace-nowrap">{p.display_name}<div className="text-[10px] text-slate-400">{p.job_title}</div></td>
                  {days.map((dstr) => (
                    <td key={dstr} className="px-2 py-2 min-w-[92px]">
                      <div className="space-y-1">
                        {(shiftsByPersonDay[p.person_id]?.[dstr] || []).map((s) => (
                          <button key={s.shift_id} onClick={() => canManage && setEditing(s)} data-testid={`shift-chip-${s.shift_id}`}
                            className={`w-full text-left text-xs rounded-lg px-1.5 py-1 border ${(s.warnings || []).length ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                            <span className="font-medium">{s.start}–{s.end}</span>
                            {(s.warnings || []).length ? <AlertTriangle size={11} className="inline ml-1 text-amber-500" /> : null}
                            {s.publication === "published_with_changes" && <span className="block text-[10px] text-amber-700" data-testid={`shift-unpublished-edit-${s.shift_id}`}>edited · not yet published</span>}
                            {s.publication === "draft_new" && <span className="block text-[10px] text-slate-500" data-testid={`shift-draft-${s.shift_id}`}>private draft · not yet published</span>}
                            {s.publication === "published" && rw.status !== "published" && <span className="block text-[10px] text-emerald-700" data-testid={`shift-visible-${s.shift_id}`}>visible to staff</span>}
                            {s.pending_person_name ? <span className="block text-[10px] text-sky-600 flex items-center gap-0.5"><Repeat size={9} /> → {s.pending_person_name}</span> : null}
                          </button>
                        ))}
                        {canManage && <button onClick={() => setEditing({ site_id: siteId, person_id: p.person_id, date: dstr, start: "09:00", end: "17:00", unpaid_break_minutes: 0, department: p.department })}
                          data-testid={`add-shift-${p.person_id}-${dstr}`} className="w-full text-[11px] text-slate-400 hover:text-brand border border-dashed rounded-lg py-0.5">+ Add</button>}
                      </div>
                    </td>
                  ))}
                  <td className="px-2 py-2 font-semibold text-slate-700">{totals?.scheduled_hours ?? 0}h{totals?.difference != null ? <div className={`text-[10px] ${totals.difference < 0 ? "text-amber-500" : "text-emerald-500"}`}>{totals.difference > 0 ? "+" : ""}{totals.difference}</div> : null}</td>
                </tr>
              );
            })}
            <PlaceholderRow days={days} placeholders={data.placeholders} siteId={siteId} canManage={canManage} people={data.people} onChanged={load} />
          </tbody>
        </table>
        </div>
      </div>
      <div className="text-xs text-slate-500" data-testid="rota-total">Total scheduled: {data.hours_summary?.total_hours ?? 0}h</div>
      {editing && <ShiftModal shift={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {confirmPublish && <PublishSummaryDialog siteId={siteId} weekStart={weekStart} onCancel={() => setConfirmPublish(false)} onConfirm={doPublish} />}
      {quickFill && <QuickFillModal siteId={siteId} weekStart={weekStart} days={days} templates={templates} people={data.people || []} shiftsByPersonDay={shiftsByPersonDay} onClose={() => setQuickFill(false)} onSaved={() => { setQuickFill(false); load(); }} />}
    </div>
  );
}

function ShiftModal({ shift, sites, pick, onClose, onSaved }) {
  const [f, setF] = useState({ start: "09:00", end: "17:00", unpaid_break_minutes: 0, department: "Kitchen", role: "", notes: "", ...shift });
  const [warnings, setWarnings] = useState(shift.warnings || []);
  const [people, setPeople] = useState([]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const needPick = pick && !f.shift_id;
  useEffect(() => {
    if (needPick && f.site_id) api.get(`/workforce/people?site_id=${f.site_id}&active=true`).then((r) => setPeople(r.data)).catch(() => setPeople([]));
  }, [needPick, f.site_id]);
  const save = async () => {
    if (needPick && (!f.site_id || !f.person_id)) return toast.error("Choose a site and a person");
    try {
      const res = f.shift_id
        ? await api.put(`/workforce/shifts/${f.shift_id}`, f)
        : await api.post("/workforce/shifts", f);
      setWarnings(res.data.warnings || []);
      if ((res.data.warnings || []).length) toast.warning("Shift saved with warnings");
      else toast.success("Shift saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async () => { try { await api.delete(`/workforce/shifts/${f.shift_id}`); toast.success("Shift deleted"); onSaved(); } catch (e) { toast.error("Delete failed"); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="shift-modal">
        <DialogHeader><DialogTitle>{f.shift_id ? "Edit shift" : "New shift"}{f.date ? ` · ${f.date}` : ""}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {needPick && (
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Site</Label>
                <Select value={f.site_id || ""} onValueChange={(v) => setF({ ...f, site_id: v, person_id: "" })}><SelectTrigger data-testid="shift-site"><SelectValue placeholder="Site" /></SelectTrigger><SelectContent>{(sites || []).map((s) => <SelectItem key={s.venue_id} value={s.venue_id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Person</Label>
                <Select value={f.person_id || ""} onValueChange={(v) => set("person_id", v)}><SelectTrigger data-testid="shift-person"><SelectValue placeholder="Person" /></SelectTrigger><SelectContent>{people.map((p) => <SelectItem key={p.person_id} value={p.person_id}>{p.display_name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Date</Label><Input type="date" value={f.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="shift-date" /></div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Start</Label><Input type="time" value={f.start} onChange={(e) => set("start", e.target.value)} data-testid="shift-start" /></div>
            <div><Label className="text-xs">End</Label><Input type="time" value={f.end} onChange={(e) => set("end", e.target.value)} data-testid="shift-end" /></div>
            <div><Label className="text-xs">Unpaid break (min)</Label><Input type="number" min="0" value={f.unpaid_break_minutes} onChange={(e) => set("unpaid_break_minutes", e.target.value)} data-testid="shift-break" /></div>
            <div><Label className="text-xs">Paid break (min)</Label><Input type="number" min="0" value={f.paid_break_minutes ?? 0} onChange={(e) => set("paid_break_minutes", e.target.value)} data-testid="shift-paid-break" /></div>
          </div>
          <div className="text-[11px] text-slate-500" data-testid="shift-break-note">Payable time = shift length − unpaid break. Paid breaks are inside the shift and stay payable.</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Department</Label>
              <Select value={f.department} onValueChange={(v) => set("department", v)}><SelectTrigger data-testid="shift-dept"><SelectValue /></SelectTrigger><SelectContent>{DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Role</Label><Input value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="shift-role" /></div>
          </div>
          <div><Label className="text-xs">Notes</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="shift-notes" /></div>
          {warnings.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 space-y-1" data-testid="shift-warnings">
            {warnings.map((w, i) => <div key={i} className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle size={12} /> {w.message}</div>)}
          </div>}
        </div>
        <DialogFooter className="flex justify-between">
          {f.shift_id ? <Button variant="outline" className="text-red-600" onClick={del} data-testid="shift-delete"><Trash2 size={14} className="mr-1" /> Delete</Button> : <span />}
          <div className="flex gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-brand hover:bg-brand-hover" onClick={save} data-testid="shift-save">Save shift</Button></div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StaffDirectory({ siteId, sites, canManage, canCreateLogin }) {
  const [people, setPeople] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/workforce/people?site_id=${siteId}${showInactive ? "" : "&active=true"}`).then((r) => setPeople(r.data)).catch(() => setPeople([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siteId, showInactive]);
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-xs text-slate-500 flex items-center gap-2"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="staff-show-inactive" /> Show inactive</label>
        {canManage && <Button size="sm" className="bg-brand hover:bg-brand-hover" data-testid="add-staff-btn" onClick={() => setEditing({ site_ids: [siteId], department: "Kitchen" })}><Plus size={14} className="mr-1" /> Add staff</Button>}
      </div>
      <div className="bg-white border rounded-2xl divide-y">
        {people.length === 0 && <div className="p-6 text-center text-slate-400" data-testid="staff-empty">No staff yet.</div>}
        {people.map((p) => (
          <div key={p.person_id} className="flex items-center justify-between px-4 py-3" data-testid={`staff-${p.person_id}`}>
            <div><div className="font-medium text-slate-800">{p.display_name} {!p.active && <span className="text-[10px] text-slate-400">(inactive)</span>}</div>
              <div className="text-xs text-slate-500">{p.job_title || "—"} · {p.department}{p.contracted_hours != null ? ` · ${p.contracted_hours}h/wk` : ""}</div></div>
            {canManage && <Button variant="outline" size="sm" onClick={() => setEditing(p)} data-testid={`edit-staff-${p.person_id}`}><Pencil size={13} className="mr-1" /> Edit</Button>}
          </div>
        ))}
      </div>
      {editing && <StaffModal person={editing} sites={sites} canCreateLogin={canCreateLogin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function StaffModal({ person, sites, canCreateLogin, onClose, onSaved }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", job_title: "", department: "Kitchen", contracted_hours: "", payroll_ref: "", ref_code: "", site_ids: [], ...person });
  const [link, setLink] = useState(null); // {linked_user, candidates}
  const [selUser, setSelUser] = useState("");
  const [giveAccess, setGiveAccess] = useState(false);
  const [accessRole, setAccessRole] = useState("staff");
  const [createdInvite, setCreatedInvite] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleSite = (sid) => set("site_ids", (f.site_ids || []).includes(sid) ? f.site_ids.filter((x) => x !== sid) : [...(f.site_ids || []), sid]);
  const loadLink = () => { if (f.person_id) api.get(`/workforce/people/${f.person_id}/link-candidates`).then((r) => setLink(r.data)).catch(() => setLink(null)); };
  useEffect(() => { loadLink(); /* eslint-disable-next-line */ }, [f.person_id]);
  const createAccess = async (pid) => {
    if (!canCreateLogin) throw new Error("not_authorised");
    if (!f.email || !f.email.includes("@")) {
      const err = new Error("email_required");
      err.userMessage = "An email address is required for CaterCTRL access";
      throw err;
    }
    const { data } = await api.post(`/workforce/people/${pid}/create-login`, { email: f.email, role: accessRole });
    setCreatedInvite(data);
    setShowInvite(false);
    toast.success("CaterCTRL invitation created");
    return data;
  };
  const save = async () => {
    try {
      if (f.person_id) {
        await api.put(`/workforce/people/${f.person_id}`, f);
        toast.success("Staff saved");
        onSaved();
        return;
      }
      const { data: created } = await api.post("/workforce/people", f);
      if (giveAccess) {
        await createAccess(created.person_id);
        setF((prev) => ({ ...prev, person_id: created.person_id, active: true }));
        return;
      }
      toast.success("Workforce person saved");
      onSaved();
    } catch (e) { toast.error(e.userMessage || e.response?.data?.detail || "Save failed"); }
  };
  const doLink = async () => {
    if (!selUser) return toast.error("Choose a login to link");
    try { await api.post(`/workforce/people/${f.person_id}/link`, { user_id: selUser }); toast.success("Login linked"); setSelUser(""); loadLink(); }
    catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
  };
  const doUnlink = async () => { try { await api.post(`/workforce/people/${f.person_id}/unlink`); toast.success("Login unlinked"); loadLink(); } catch (e) { toast.error("Unlink failed"); } };
  const createAccessForExisting = async () => {
    try {
      await createAccess(f.person_id);
      loadLink();
    } catch (e) { toast.error(e.userMessage || e.response?.data?.detail || "Could not create CaterCTRL access"); }
  };
  const copyInvite = async () => {
    try { await navigator.clipboard.writeText(createdInvite.invite_link); toast.success("Invitation link copied"); }
    catch { toast.error("Could not copy invitation link"); }
  };
  const deactivate = async () => { try { await api.post(`/workforce/people/${f.person_id}/deactivate`, { active: !f.active }); toast.success("Updated"); onSaved(); } catch (e) { toast.error("Failed"); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="staff-modal">
        <DialogHeader><DialogTitle>{f.person_id ? "Edit staff" : "Add staff"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">First name</Label><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="staff-first" /></div>
            <div><Label className="text-xs">Last name</Label><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="staff-last" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Job title</Label><Input value={f.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="staff-job" /></div>
            <div><Label className="text-xs">Department</Label>
              <Select value={f.department} onValueChange={(v) => set("department", v)}><SelectTrigger data-testid="staff-dept"><SelectValue /></SelectTrigger><SelectContent>{DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="staff-email" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Contracted hours/wk</Label><Input type="number" value={f.contracted_hours ?? ""} onChange={(e) => set("contracted_hours", e.target.value)} data-testid="staff-contracted" /></div>
            <div><Label className="text-xs">Payroll ref</Label><Input value={f.payroll_ref} onChange={(e) => set("payroll_ref", e.target.value)} /></div>
          </div>
          {!f.person_id && canCreateLogin && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="staff-give-access-section">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={giveAccess} onChange={(e) => setGiveAccess(e.target.checked)} data-testid="staff-give-access" className="mt-1" />
                <span className="text-sm text-slate-700"><span className="font-medium">Give this person CaterCTRL access</span>
                  <span className="block text-xs text-slate-500">Creates a login linked to this same Workforce profile — no duplicate employee record.</span></span>
              </label>
              {giveAccess && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div><Label className="text-xs">Login role</Label>
                    <Select value={accessRole} onValueChange={setAccessRole}><SelectTrigger data-testid="staff-access-role"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="admin">Owner / Admin</SelectItem></SelectContent>
                    </Select></div>
                  <div className="text-xs text-slate-500 flex items-end pb-2">Site access is taken from this Workforce profile.</div>
                </div>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs">Sites</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {sites.map((s) => <button key={s.venue_id} onClick={() => toggleSite(s.venue_id)} data-testid={`staff-site-${s.venue_id}`}
                className={`text-xs px-2 py-1 rounded-lg border ${(f.site_ids || []).includes(s.venue_id) ? "bg-brand text-white border-brand" : "border-slate-200 text-slate-600"}`}>{s.name}</button>)}
            </div>
          </div>
          {f.person_id && (
            <div className="border rounded-xl p-3 bg-slate-50" data-testid="staff-link-section">
              <Label className="text-xs font-semibold text-slate-600">CaterCTRL login</Label>
              {link?.linked_user ? (
                <div className="flex items-center justify-between mt-2" data-testid="staff-linked">
                  <div className="text-sm min-w-0"><span className="font-medium text-slate-800">{link.linked_user.name}</span>
                    <div className="text-xs text-slate-500 truncate">{link.linked_user.email}</div></div>
                  <Button variant="outline" size="sm" onClick={doUnlink} data-testid="staff-unlink">Unlink</Button>
                </div>
              ) : (
                <div className="space-y-3 mt-2" data-testid="staff-not-linked">
                  <div className="flex items-center gap-2">
                    <Select value={selUser} onValueChange={setSelUser}><SelectTrigger data-testid="staff-link-select"><SelectValue placeholder="Link an existing login" /></SelectTrigger>
                      <SelectContent>{(link?.candidates || []).map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name} · {c.email}</SelectItem>)}</SelectContent></Select>
                    <Button size="sm" variant="outline" className="shrink-0" onClick={doLink} data-testid="staff-link-btn">Link</Button>
                  </div>
                  {canCreateLogin && (
                    <div className="border-t pt-3">
                      <div className="text-xs font-medium text-slate-700 mb-2">Or give this person new CaterCTRL access</div>
                      <div className="flex items-center gap-2">
                        <Select value={accessRole} onValueChange={setAccessRole}><SelectTrigger data-testid="staff-existing-access-role"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="admin">Owner / Admin</SelectItem></SelectContent>
                        </Select>
                        <Button size="sm" className="bg-brand hover:bg-brand-hover shrink-0" onClick={createAccessForExisting} data-testid="staff-create-login">Create access</Button>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Uses this person's email and site assignments. A new linked Workforce profile is not created.</p>
                    </div>
                  )}
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-1">Linking lets this person see their rota and request swaps. Unlinking never deletes the account or past shifts.</p>
            </div>
          )}
          {createdInvite && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="workforce-invite-ready">
              <div className="font-semibold text-sm text-emerald-900">CaterCTRL invitation ready</div>
              <p className="text-xs text-emerald-800 mt-1">The Workforce person and CaterCTRL login are linked. Send this single-use set-password link securely; it expires after 72 hours.</p>
              <div className="flex gap-2 mt-2">
                <Input readOnly value={showInvite ? createdInvite.invite_link : "••••••••••••••••••••••••••••••••"} className="font-mono text-xs bg-white" data-testid="workforce-invite-link" />
                <Button type="button" variant="outline" size="icon" onClick={() => setShowInvite((v) => !v)} aria-label={showInvite ? "Hide invitation link" : "Show invitation link"}>{showInvite ? <EyeOff size={15} /> : <Eye size={15} />}</Button>
                <Button type="button" onClick={copyInvite} data-testid="workforce-copy-invite"><Copy size={15} className="mr-1" /> Copy</Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex justify-between">
          {f.person_id && !createdInvite ? <Button variant="outline" onClick={deactivate} data-testid="staff-deactivate">{f.active ? "Deactivate" : "Reactivate"}</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={createdInvite ? onSaved : onClose}>{createdInvite ? "Done" : "Cancel"}</Button>
            {!createdInvite && <Button className="bg-brand hover:bg-brand-hover" onClick={save} data-testid="staff-save">Save</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesPanel({ sites, canManage }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/workforce/templates").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="templates-panel">
      {canManage && <div className="flex justify-end"><Button size="sm" className="bg-brand hover:bg-brand-hover" data-testid="add-template-btn" onClick={() => setEditing({ start: "09:00", end: "17:00", unpaid_break_minutes: 30, department: "Kitchen" })}><Plus size={14} className="mr-1" /> New template</Button></div>}
      <div className="bg-white border rounded-2xl divide-y">
        {items.length === 0 && <div className="p-6 text-center text-slate-400" data-testid="templates-empty">No shift templates yet.</div>}
        {items.map((t) => (
          <div key={t.template_id} className="flex items-center justify-between px-4 py-3" data-testid={`template-${t.template_id}`}>
            <div><div className="font-medium text-slate-800">{t.name} {!t.active && <span className="text-[10px] text-slate-400">(inactive)</span>}</div>
              <div className="text-xs text-slate-500">{t.start}–{t.end} · {t.unpaid_break_minutes}m unpaid{t.paid_break_minutes ? ` · ${t.paid_break_minutes}m paid` : ""} · {t.department}{t.role ? ` · ${t.role}` : ""}</div></div>
            {canManage && <Button variant="outline" size="sm" onClick={() => setEditing(t)} data-testid={`edit-template-${t.template_id}`}><Pencil size={13} className="mr-1" /> Edit</Button>}
          </div>
        ))}
      </div>
      {editing && <TemplateModal tpl={editing} sites={sites} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TemplateModal({ tpl, sites, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", start: "09:00", end: "17:00", unpaid_break_minutes: 30, paid_break_minutes: 0, department: "Kitchen", role: "", site_id: "", notes: "", ...tpl });
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    try {
      const body = { ...f, site_id: f.site_id || null };
      if (f.template_id) await api.put(`/workforce/templates/${f.template_id}`, body); else await api.post("/workforce/templates", body);
      toast.success("Template saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deact = async () => { try { await api.post(`/workforce/templates/${f.template_id}/deactivate`, { active: !f.active }); toast.success("Updated"); onSaved(); } catch (e) { toast.error("Failed"); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="template-modal">
        <DialogHeader><DialogTitle>{f.template_id ? "Edit template" : "New shift template"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="template-name" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Start</Label><Input type="time" value={f.start} onChange={(e) => set("start", e.target.value)} data-testid="template-start" /></div>
            <div><Label className="text-xs">End</Label><Input type="time" value={f.end} onChange={(e) => set("end", e.target.value)} data-testid="template-end" /></div>
            <div><Label className="text-xs">Unpaid break (min)</Label><Input type="number" min="0" value={f.unpaid_break_minutes} onChange={(e) => set("unpaid_break_minutes", e.target.value)} data-testid="template-unpaid-break" /></div>
            <div><Label className="text-xs">Paid break (min)</Label><Input type="number" min="0" value={f.paid_break_minutes ?? 0} onChange={(e) => set("paid_break_minutes", e.target.value)} data-testid="template-paid-break" /></div>
          </div>
          <div className="text-[11px] text-slate-500" data-testid="template-break-note">Unpaid breaks are deducted from payable hours; paid breaks stay payable. Templates saved before paid breaks existed keep their unpaid break only.</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Department</Label>
              <Select value={f.department} onValueChange={(v) => set("department", v)}><SelectTrigger data-testid="template-dept"><SelectValue /></SelectTrigger><SelectContent>{DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Role</Label><Input value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="template-role" /></div>
          </div>
        </div>
        <DialogFooter className="flex justify-between">
          {f.template_id ? <Button variant="outline" onClick={deact} data-testid="template-deactivate">{f.active ? "Deactivate" : "Reactivate"}</Button> : <span />}
          <div className="flex gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-brand hover:bg-brand-hover" onClick={save} data-testid="template-save">Save</Button></div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CR_STATUS = {
  awaiting_colleague: "Awaiting colleague",
  awaiting_manager: "Awaiting manager approval",
  approved: "Approved",
  approved_awaiting_republish: "Approved — awaiting republish",
  declined: "Declined",
  cancelled: "Cancelled",
};
const crKind = (r) => (r.kind === "swap" ? "Swap" : r.cover_mode === "nominated" ? "Cover (asked)" : "Cover (open)");

function RequestsPanel({ canManage }) {
  const [items, setItems] = useState([]);
  const [changes, setChanges] = useState([]);
  const load = () => {
    api.get("/workforce/requests").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/workforce/change-requests?box=manage").then((r) => setChanges(r.data)).catch(() => setChanges([]));
  };
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { const r = await api.post(`/workforce/requests/${id}/${action}`, {});
      if (action === "approve" && r.data.affected_shifts?.length) toast.warning(`Approved — ${r.data.affected_shifts.length} existing shift(s) affected`);
      else toast.success(`Request ${action}d`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const crAct = async (id, action) => {
    try {
      const r = await api.post(`/workforce/change-requests/${id}/${action}`, action === "approve" ? { operation_id: (crypto.randomUUID?.() || `op_${Date.now()}`) } : {});
      if (action === "approve") {
        if (r.data.status === "approved_awaiting_republish") toast.success("Approved — publish the rota to apply the change");
        else toast.success("Approved and applied");
        if ((r.data.warnings || []).length) toast.warning(`${r.data.warnings.length} scheduling warning(s) — please review`);
      } else toast.success("Declined");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const swaps = changes.filter((r) => ["awaiting_colleague", "awaiting_manager"].includes(r.status));
  const swapHistory = changes.filter((r) => !["awaiting_colleague", "awaiting_manager"].includes(r.status));
  return (
    <div className="space-y-6" data-testid="requests-panel">
      {/* Swap & cover requests */}
      <div>
        <h3 className="font-semibold text-slate-700 mb-2 flex items-center gap-2"><Repeat size={15} className="text-brand" /> Swap &amp; cover requests</h3>
        <div className="bg-white border rounded-2xl divide-y">
          {swaps.length === 0 && <div className="p-6 text-center text-slate-400" data-testid="change-requests-empty">No swap or cover requests waiting.</div>}
          {swaps.map((r) => (
            <div key={r.request_id} className="flex items-center justify-between px-4 py-3" data-testid={`change-request-${r.request_id}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-800 truncate">{crKind(r)} · {r.requester_person_name}</div>
                <div className="text-xs text-slate-500 truncate">
                  {r.shift_public?.date} {r.shift_public?.start}–{r.shift_public?.end} @ {r.shift_public?.site_name}
                  {r.kind === "swap" && r.target_shift_public ? ` ⇄ ${r.target_person_name} ${r.target_shift_public.date} ${r.target_shift_public.start}–${r.target_shift_public.end}` : ""}
                  {r.kind === "cover" && r.volunteer_person_name ? ` · ${r.volunteer_person_name} to cover` : ""}
                  {r.note ? ` · “${r.note}”` : ""}
                </div>
                <span className="text-[10px] uppercase font-semibold text-amber-500">{CR_STATUS[r.status]}</span>
              </div>
              {canManage && r.status === "awaiting_manager" && (
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" className="bg-brand hover:bg-brand-hover" data-testid={`cr-approve-${r.request_id}`} onClick={() => crAct(r.request_id, "approve")}><Check size={14} className="mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" data-testid={`cr-decline-${r.request_id}`} onClick={() => crAct(r.request_id, "decline")}>Decline</Button>
                </div>
              )}
              {r.status === "awaiting_colleague" && <span className="text-xs text-slate-400 shrink-0">Waiting on colleague</span>}
            </div>
          ))}
          {swapHistory.map((r) => (
            <div key={r.request_id} className="flex items-center justify-between px-4 py-2 opacity-70" data-testid={`change-request-${r.request_id}`}>
              <div className="text-xs text-slate-500 truncate">{crKind(r)} · {r.requester_person_name} · {r.shift_public?.date}</div>
              <span className="text-[10px] uppercase font-semibold text-slate-400 shrink-0">{CR_STATUS[r.status]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Availability & time-off */}
      <div>
        <h3 className="font-semibold text-slate-700 mb-2">Availability &amp; time-off</h3>
        <div className="bg-white border rounded-2xl divide-y">
          {items.length === 0 && <div className="p-6 text-center text-slate-400" data-testid="requests-empty">No requests.</div>}
          {items.map((r) => (
            <div key={r.request_id} className="flex items-center justify-between px-4 py-3" data-testid={`request-${r.request_id}`}>
              <div>
                <div className="font-medium text-slate-800">{r.person_name} · <span className="capitalize">{r.type}</span></div>
                <div className="text-xs text-slate-500">
                  {r.type === "date" ? `${r.date_from}${r.date_to !== r.date_from ? `–${r.date_to}` : ""} ${r.available ? "custom" : "unavailable"}` : `${(r.recurring || []).length} weekday rule(s)`}
                  {r.note ? ` · "${r.note}"` : ""}
                </div>
                <span className={`text-[10px] uppercase font-semibold ${r.status === "pending" ? "text-amber-500" : r.status === "approved" ? "text-emerald-500" : "text-slate-400"}`}>{r.status}</span>
              </div>
              {canManage && r.status === "pending" && (
                <div className="flex gap-2">
                  <Button size="sm" className="bg-brand hover:bg-brand-hover" data-testid={`approve-${r.request_id}`} onClick={() => act(r.request_id, "approve")}>Approve</Button>
                  <Button size="sm" variant="outline" data-testid={`decline-${r.request_id}`} onClick={() => act(r.request_id, "decline")}>Decline</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CombinedPanel({ sites, canManage }) {
  const [weekStart, setWeekStart] = useState(iso(mondayOf(new Date())));
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/workforce/rota/combined?week_start=${weekStart}`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);
  if (!data) return <p className="text-slate-400" data-testid="combined-loading">Loading combined view…</p>;
  return (
    <div className="space-y-3" data-testid="combined-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="combined-prev" onClick={() => setWeekStart(iso(addDays(new Date(weekStart), -7)))}><ChevronLeft size={16} /></Button>
          <span className="font-semibold text-slate-700" data-testid="combined-week-label">Week of {weekStart}</span>
          <Button variant="outline" size="icon" data-testid="combined-next" onClick={() => setWeekStart(iso(addDays(new Date(weekStart), 7)))}><ChevronRight size={16} /></Button>
        </div>
        {canManage && <Button size="sm" className="bg-brand hover:bg-brand-hover" data-testid="combined-add-shift" onClick={() => setEditing({ __pick: true, date: weekStart, start: "09:00", end: "17:00", unpaid_break_minutes: 0, department: "Kitchen" })}><Plus size={14} className="mr-1" /> Add shift</Button>}
      </div>
      {(data.cross_site_conflicts || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 space-y-1" data-testid="combined-conflicts">
          {data.cross_site_conflicts.map((c, i) => <div key={i} className="flex items-center gap-1"><AlertTriangle size={12} /> {c.person_name}: {c.a.site} {c.a.date} {c.a.start}–{c.a.end} overlaps {c.b.site} {c.b.start}–{c.b.end}</div>)}
        </div>
      )}
      <div className="bg-white border rounded-2xl divide-y">
        {(data.people || []).length === 0 && <div className="p-6 text-center text-slate-400" data-testid="combined-empty">No shifts scheduled across your sites this week.</div>}
        {(data.people || []).map((row) => (
          <div key={row.person_id} className="px-4 py-3" data-testid={`combined-person-${row.person_id}`}>
            <div className="flex items-center justify-between">
              <div className="font-medium text-slate-800">{row.person_name}</div>
              <div className="text-xs text-slate-500">{row.hours}h · {(row.sites || []).length} site(s)</div>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {row.shifts.sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start)).map((s) => (
                <button key={s.shift_id} onClick={() => canManage && setEditing(s)} data-testid={`combined-shift-${s.shift_id}`}
                  className="text-xs rounded-lg px-2 py-1 border border-slate-200 bg-slate-50 hover:border-brand text-left">
                  <span className="font-medium">{s.date.slice(5)} {s.start}–{s.end}</span>
                  <span className="block text-[10px] text-slate-400">{s.site_name}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="text-xs text-slate-500" data-testid="combined-total">Total across sites: {data.total_hours}h</div>
      {editing && <ShiftModal shift={editing} sites={sites} pick={!!editing.__pick} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function QuickFillModal({ siteId, weekStart, days, templates, people, shiftsByPersonDay, onClose, onSaved }) {
  const [templateId, setTemplateId] = useState(templates[0]?.template_id || "");
  const [selDays, setSelDays] = useState(days.slice(0, 5));
  const [selPeople, setSelPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const targets = [];
  selPeople.forEach((pid) => selDays.forEach((d) => { if (!(shiftsByPersonDay[pid]?.[d] || []).length) targets.push({ person_id: pid, date: d }); }));
  const apply = async () => {
    if (!templateId) return toast.error("Choose a template");
    if (targets.length === 0) return toast.error("No empty cells selected");
    setBusy(true);
    try {
      const res = (await api.post("/workforce/rota/quick-fill", {
        site_id: siteId, template_id: templateId, week_start: weekStart, targets,
        operation_id: (crypto.randomUUID?.() || `op_${Date.now()}`),
      })).data;
      toast.success(`Filled ${res.created} shift(s)${res.skipped ? ` · ${res.skipped} skipped` : ""}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Quick-fill failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="quick-fill-modal">
        <DialogHeader><DialogTitle>Quick-fill from template</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs">Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}><SelectTrigger data-testid="qf-template"><SelectValue placeholder="Choose a template" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.name} · {t.start}–{t.end}</SelectItem>)}</SelectContent></Select></div>
          <div>
            <Label className="text-xs">Days</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {days.map((d, i) => <button key={d} onClick={() => toggle(selDays, setSelDays, d)} data-testid={`qf-day-${d}`}
                className={`text-xs px-2 py-1 rounded-lg border ${selDays.includes(d) ? "bg-brand text-white border-brand" : "border-slate-200 text-slate-600"}`}>{DAYS[i]} {d.slice(5)}</button>)}
            </div>
          </div>
          <div>
            <Label className="text-xs">Staff</Label>
            <div className="flex flex-wrap gap-2 mt-1 max-h-40 overflow-y-auto">
              {people.length === 0 && <span className="text-xs text-slate-400">No staff assigned to this site.</span>}
              {people.map((p) => <button key={p.person_id} onClick={() => toggle(selPeople, setSelPeople, p.person_id)} data-testid={`qf-person-${p.person_id}`}
                className={`text-xs px-2 py-1 rounded-lg border ${selPeople.includes(p.person_id) ? "bg-brand text-white border-brand" : "border-slate-200 text-slate-600"}`}>{p.display_name}</button>)}
            </div>
          </div>
          <div className="text-xs text-slate-500" data-testid="qf-target-count">{targets.length} empty cell(s) will be filled (existing shifts are left untouched).</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-hover" onClick={apply} disabled={busy} data-testid="qf-apply">Fill {targets.length || ""} shift(s)</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
